import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import './ActiveSubscriptionCard.css';

import { formatDateOnly } from '../../utils/dateUtils';
const formatMobileDate = (date) => date
    ? formatDateOnly(date)
    : 'N/A';

export const ActiveSubscriptionCard = ({ activeSub, vendor, onBuyExtraLeads, onRenewSubscription }) => {
    const hasActiveSub = Boolean(activeSub && activeSub.Plan);
    const isTrial = hasActiveSub && activeSub?.Plan?.is_trial;
    const isExpiringSoon = hasActiveSub && (new Date(activeSub.end_date) - new Date()) / (1000 * 60 * 60 * 24) < 2;
    const includedLeads = Number(activeSub?.Plan?.leads_included || 0);
    const availableLeads = Number(vendor?.available_leads || 0);
    const sentLeads = vendor?.used_leads !== undefined
        ? Number(vendor.used_leads)
        : Math.max(includedLeads - availableLeads, 0);

    const handleActionClick = () => {
        if (hasActiveSub) {
            if (onBuyExtraLeads) onBuyExtraLeads();
        } else {
            if (onRenewSubscription) {
                onRenewSubscription();
            } else if (onBuyExtraLeads) {
                onBuyExtraLeads();
            }
        }
    };

    return (
        <>
            {/* Mobile card */}
            <div className={`subscription-mobile-card d-block d-md-none ${!hasActiveSub ? 'no-active-sub' : ''}`}>
                <div className={`subscription-mobile-header ${!hasActiveSub ? 'bg-danger text-white' : ''}`}>
                    <span><i className="ri-vip-crown-2-line me-1"></i> Active Plan: {hasActiveSub ? activeSub.Plan.name : 'No Active Subscription'}</span>
                    <span className={`subscription-mobile-status ${!hasActiveSub ? 'bg-dark' : ''}`}>
                        {hasActiveSub ? 'Active' : 'Inactive'}
                    </span>
                </div>
                <div className="subscription-mobile-grid">
                    <div className="subscription-mobile-cell">
                        <span className="subscription-mobile-label">Start Date:</span>
                        <span className="subscription-mobile-value">{hasActiveSub ? formatMobileDate(activeSub.start_date) : 'N/A'}</span>
                    </div>
                    <div className="subscription-mobile-cell">
                        <span className="subscription-mobile-label">End Date:</span>
                        <span className="subscription-mobile-value">{hasActiveSub ? formatMobileDate(activeSub.end_date) : 'N/A'}</span>
                    </div>
                    <div className="subscription-mobile-cell">
                        <span className="subscription-mobile-label">Sent:</span>
                        <span className="subscription-mobile-value subscription-mobile-leads">{sentLeads} / {availableLeads}</span>
                    </div>
                    <div className="subscription-mobile-cell">
                        <span className="subscription-mobile-label">Plan Amount: </span>
                        <span className="subscription-mobile-value">
                            {hasActiveSub ? `₹ ${activeSub.Plan.price} / ${activeSub.Plan.duration_days} Days` : 'N/A'}
                        </span>
                    </div>
                </div>
                <button type="button" className={`subscription-mobile-buy ${!hasActiveSub ? 'bg-danger text-white border-danger' : ''}`} onClick={handleActionClick}>
                    <i className={hasActiveSub ? "ri-add-line me-1" : "ri-refresh-line me-1"}></i>
                    {hasActiveSub ? 'Buy Extra Leads' : 'Renew Subscription'}
                </button>
            </div>
            {/* Desktop card */}
            <Card className="mb-4 border-0 shadow-sm overflow-hidden subscription-desktop-card d-none d-md-block">
                <div className={`card-header ${hasActiveSub ? (isTrial ? 'bg-info' : 'bg-primary') : 'bg-danger'} text-white`}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 text-white">
                            Active Plan: {hasActiveSub ? activeSub.Plan.name : 'No Active Subscription'}
                        </h5>
                        {hasActiveSub && isTrial && <Badge bg="light" text="dark">FREE TRIAL</Badge>}
                        {!hasActiveSub && <Badge bg="light" text="danger">NO ACTIVE PLAN</Badge>}
                    </div>
                </div>
                <Card.Body>
                    <Row className="align-items-center g-5 subscription-active-details">
                        <Col xs={6} md="auto" className="subscription-active-detail">
                            <p className="text-muted mb-1">Status</p>
                            <Badge bg={hasActiveSub ? "success" : "danger"} className="fs-12">
                                {hasActiveSub ? "ACTIVE" : "INACTIVE"}
                            </Badge>
                        </Col>
                        <Col xs={6} md="auto" className="subscription-active-detail">
                            <p className="text-muted mb-1">Start Date</p>
                            <h6 className="mb-0">{hasActiveSub ? formatMobileDate(activeSub.start_date) : 'N/A'}</h6>
                        </Col>
                        <Col xs={6} md="auto" className="subscription-active-detail">
                            <p className="text-muted mb-1">End Date</p>
                            <h6 className={`mb-0 ${isExpiringSoon ? 'text-danger' : ''}`}>
                                {hasActiveSub ? formatMobileDate(activeSub.end_date) : 'N/A'}
                            </h6>
                        </Col>
                        <Col xs={6} md="auto" className="text-center subscription-active-detail">
                            <p className="text-muted mb-1">Leads Sent</p>
                            <Link
                                to="/admin/my-leads"
                                className="fw-semibold text-primary text-decoration-underline"
                                title="View My Leads"
                            >
                                {sentLeads} / {availableLeads}
                            </Link>
                        </Col>
                        <Col xs={6} md="auto" className="ms-md-auto text-center subscription-active-detail">
                            <p className="text-muted mb-1">Plan Amount</p>
                            <h5 className="mb-0">
                                {hasActiveSub ? (
                                    <>
                                        ₹{activeSub.Plan.price}{' '}
                                        <span className="fs-13 text-muted">/ {activeSub.Plan.duration_days} Days</span>
                                    </>
                                ) : (
                                    'N/A'
                                )}
                            </h5>
                        </Col>
                        <Col xs={12} md="auto" className="text-md-end subscription-active-detail">
                            <Button
                                variant={hasActiveSub ? "light" : "danger"}
                                className={hasActiveSub ? "border text-primary fw-semibold" : "fw-semibold text-white"}
                                onClick={handleActionClick}
                            >
                                <i className={hasActiveSub ? "ri-add-line me-1" : "ri-refresh-line me-1"}></i>
                                {hasActiveSub ? "Buy Extra Leads" : "Renew Subscription"}
                            </Button>
                        </Col>
                        {isExpiringSoon && (
                            <Col xs={12}>
                                <Alert variant="warning" className="mt-3 mb-0 py-2">
                                    <i className="ri-error-warning-line me-1"></i>
                                    Your plan is expiring soon. Renew now to avoid interruption.
                                </Alert>
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>
        </>
    );
};

const ActiveSubscriptionSummary = () => {
    const navigate = useNavigate();
    const [activeSub, setActiveSub] = useState(null);
    const [vendor, setVendor] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const fetchSummary = async () => {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
                const requests = [apiClient.get('/vendors/subscription/active')];

                if (userInfo?.id) {
                    requests.push(apiClient.get(`/vendors/${userInfo.id}/details`));
                }

                const [subscriptionResponse, vendorResponse] = await Promise.all(requests);
                if (!isMounted) return;

                setActiveSub(subscriptionResponse.data.subscription || null);
                setVendor({
                    ...(vendorResponse?.data?.user || userInfo || {}),
                    available_leads: subscriptionResponse.data.available_leads,
                    used_leads: subscriptionResponse.data.used_leads
                });
            } catch (error) {
                console.error('Failed to load active subscription summary:', error);
            }
        };

        fetchSummary();
        const handleBalanceUpdate = (event) => {
            if (!isMounted) return;
            const availableLeads = event.detail?.availableLeads;
            if (availableLeads !== undefined) {
                setVendor(current => current ? {
                    ...current,
                    available_leads: availableLeads,
                    used_leads: Number(current.used_leads || 0) + 1
                } : current);
            }
            // Re-read the authoritative balance so every unlock flow stays in sync.
            fetchSummary();
        };
        window.addEventListener('lead-balance-updated', handleBalanceUpdate);
        return () => {
            isMounted = false;
            window.removeEventListener('lead-balance-updated', handleBalanceUpdate);
        };
    }, []);

    return (
        <ActiveSubscriptionCard
            activeSub={activeSub}
            vendor={vendor}
            onBuyExtraLeads={() => navigate('/admin/subscriptions', { state: { openBuyLeads: true } })}
            onRenewSubscription={() => navigate('/admin/subscriptions', { state: { scrollToPlans: true } })}
        />
    );
};

export default ActiveSubscriptionSummary;
